import { useQuery } from '@tanstack/react-query'
import { useTranslation } from '@/lib/i18n'
import {
	dashboardPercentage,
	formatDashboardNumber,
	resolveDashboardLocale
} from '../-lib/dashboard-formatters'
import { dashboardTimeseriesQueryOptions } from '../-lib/dashboard-queries'
import { DashboardEmptyState } from './dashboard-empty-state'
import {
	DashboardQueryError,
	DashboardQueryLoading
} from './dashboard-query-state'

export function DashboardRequestVolume() {
	const seriesQuery = useQuery(dashboardTimeseriesQueryOptions())
	const { t, i18n } = useTranslation()
	const locale = resolveDashboardLocale(
		i18n.resolvedLanguage ?? i18n.language
	)

	return (
		<section className='portal-card dashboard-data-card dashboard-request-volume'>
			<div className='portal-card__header'>
				<h2 className='portal-card__title'>
					{t('common:dashboard.seven_day_requests')}
				</h2>
				{seriesQuery.isSuccess ? (
					<span className='portal-meta'>
						{t('common:dashboard.total_requests', {
							value: formatDashboardNumber(
								seriesQuery.data.points.reduce(
									(total, point) =>
										total + point.metrics.total,
									0
								),
								locale
							)
						})}
					</span>
				) : null}
			</div>
			{seriesQuery.isPending ? (
				<DashboardQueryLoading
					label={t('common:dashboard.timeseries_loading')}
				/>
			) : null}
			{seriesQuery.isError ? (
				<DashboardQueryError
					description={t(
						'common:dashboard.timeseries_error_description'
					)}
					isRetrying={seriesQuery.isFetching}
					onRetry={() => seriesQuery.refetch()}
					retryLabel={t(
						seriesQuery.isFetching
							? 'common:dashboard.retrying'
							: 'common:dashboard.retry'
					)}
					title={t('common:dashboard.timeseries_error_title')}
				/>
			) : null}
			{seriesQuery.isSuccess ? (
				<TimeseriesChart points={seriesQuery.data.points} />
			) : null}
		</section>
	)
}

function TimeseriesChart(props: {
	points: Array<{ bucket: string; metrics: { total: number } }>
}) {
	const { t } = useTranslation()
	if (props.points.length === 0) {
		return (
			<div className='bar-chart bar-chart--empty justify-center'>
				<DashboardEmptyState
					description={t(
						'common:dashboard.no_timeseries_description'
					)}
					icon='chart'
					title={t('common:dashboard.no_timeseries_title')}
				/>
			</div>
		)
	}

	const maxRequests = Math.max(
		...props.points.map((point) => point.metrics.total),
		1
	)
	return (
		<div
			aria-label={t('common:dashboard.timeseries_label')}
			className='bar-chart'
			role='img'
		>
			{props.points.map((point) => (
				<span
					key={point.bucket}
					style={{
						height: `${dashboardPercentage(
							point.metrics.total,
							maxRequests
						)}%`
					}}
				/>
			))}
		</div>
	)
}
